import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiMessageSquare, FiSend, FiTrash2 } from 'react-icons/fi';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

/**
 * Notes thread for a single quote request. Each call/follow-up attempt can be
 * logged here so the next person picking up the request knows what's been done.
 */
function QuoteRequestNotes({ requestId }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API_URL}/quote-requests/${requestId}/notes`, config);
      setNotes(res.data || []);
    } catch (err) {
      console.error('Failed to load notes:', err);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API_URL}/quote-requests/${requestId}/notes`, { body: trimmed }, config);
      setBody('');
      load();
    } catch (err) {
      alert('Failed to add note: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      const token = localStorage.getItem('authToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_URL}/quote-requests/notes/${noteId}`, config);
      load();
    } catch (err) {
      alert('Failed to delete note: ' + (err.response?.data?.error || err.message));
    }
  };

  const isAdmin = user?.can_manage_payroll;

  return (
    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <FiMessageSquare size={14} style={{ color: '#6b7280' }} />
        <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, letterSpacing: '0.04em' }}>
          Notes ({notes.length})
        </div>
      </div>

      {/* Notes list */}
      <div style={{ marginBottom: '12px' }}>
        {loading ? (
          <div style={{ padding: '8px 0', color: '#9ca3af', fontSize: '13px' }}>Loading...</div>
        ) : notes.length === 0 ? (
          <div style={{ padding: '8px 0', color: '#9ca3af', fontSize: '13px', fontStyle: 'italic' }}>
            No notes yet. Add the first one below.
          </div>
        ) : (
          notes.map(note => (
            <div
              key={note.id}
              style={{
                padding: '10px 12px', marginBottom: '6px', borderRadius: '8px',
                backgroundColor: '#f9fafb', border: '1px solid #f3f4f6',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px', gap: '8px' }}>
                <div style={{ fontSize: '12px', color: '#374151', fontWeight: 600 }}>
                  {note.created_by_name || 'Unknown'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                    {note.created_at ? new Date(note.created_at + 'Z').toLocaleString() : ''}
                  </div>
                  {(note.created_by_id === user?.id || isAdmin) && (
                    <button
                      onClick={() => handleDelete(note.id)}
                      title="Delete note"
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af',
                        padding: '2px 4px', borderRadius: '4px',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#dc2626'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                    >
                      <FiTrash2 size={11} />
                    </button>
                  )}
                </div>
              </div>
              <div style={{ fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                {note.body}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add note */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a note (e.g. 'Called — no answer, left voicemail')..."
          rows={2}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleAdd();
            }
          }}
          style={{
            flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px',
            fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
          }}
        />
        <button
          onClick={handleAdd}
          disabled={saving || !body.trim()}
          style={{
            padding: '8px 14px', backgroundColor: body.trim() ? '#2563eb' : '#e5e7eb',
            color: body.trim() ? 'white' : '#9ca3af', border: 'none', borderRadius: '8px',
            cursor: body.trim() && !saving ? 'pointer' : 'not-allowed',
            fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px',
          }}
        >
          <FiSend size={12} /> {saving ? 'Adding...' : 'Add'}
        </button>
      </div>
      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
        Tip: Ctrl/Cmd + Enter to submit.
      </div>
    </div>
  );
}

export default QuoteRequestNotes;
